import React from 'react';
import LegendAttributionDisclosure from './LegendDisclosure';
import USGSMarker from './USGSMarker';

type USGSMarkerState =
  | 'majorFlood'
  | 'moderateFlood'
  | 'minorFlood'
  | 'actionStage'
  | 'notFlooding'
  | 'noFloodStages'
  | 'measurement'
  | 'unavailable'
  | string;

type USGSMarkerType = 'lake' | 'stream' | 'wetland' | 'estuary' | 'coastal';

export interface USGSMapLegendProps {
  states: USGSMarkerState[];
  className?: string;
  setLegendToggle?: React.Dispatch<React.SetStateAction<boolean>>;
  style?: React.CSSProperties;
  types?: USGSMarkerType[];
}

const DEFAULT_STATES: USGSMarkerState[] = [
  'major',
  'moderate',
  'minor',
  'action',
  'notFlooding',
  'noFloodStages',
  'measurement',
  'unavailable',
];
const DEFAULT_TYPES: USGSMarkerType[] = ['lake', 'stream', 'wetland', 'estuary', 'coastal'];

function formatLabel(s: string) {
  return s
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

const STATE_LABELS: Record<string, string> = {
  major: 'In major flood stage',
  moderate: 'In moderate flood stage',
  minor: 'In minor flood stage',
  action: 'In action stage',
  notFlooding: 'Not flooding',
  noFloodStages: 'Flood stages not established',
  measurement: 'Measurement flag',
  unavailable: 'Recent measurement unavailable',
};

function formatStateLabel(state: string) {
  return STATE_LABELS[state] || formatLabel(state);
}

export default function USGSMapLegend({
  states = DEFAULT_STATES,
  types = DEFAULT_TYPES,
  className = '',
  setLegendToggle,
  style,
}: USGSMapLegendProps) {
  return (
    <div
      className={`bg-dark bg-opacity-75 text-white rounded-3 p-2 shadow-lg max-w-3xs ${className}`}
      style={{
        width: '100%',
        maxHeight: '100%',
        overflow: 'auto',
        position: 'relative',
        zIndex: 1200,
        ...style,
      }}
      role="dialog"
      aria-label="USGS map legend"
    >
      <button
        type="button"
        className="btn btn-outline-warning btn-sm position-absolute top-0 end-0 m-2 rounded-circle"
        aria-label="Close"
        onClick={() => setLegendToggle?.(false)}
        style={{ width: 26, height: 26, padding: 0, lineHeight: 1 }}
      >
        X
      </button>

      <div className="pe-2 mb-1">
        <div className="fw-semibold">Surface-Water Levels: NWS Flood</div>
      </div>

      <div className="mt-2">
        <div className="text-uppercase small opacity-75 mb-1">COLOR - FLOOD CONDITION</div>
        <div className="d-flex flex-column gap-1">
          {states.map((state) => (
            <div key={`${state}`} className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ width: 14, height: 14 }}
              >
                <USGSMarker type={'stream'} state={state} width={14} height={14} className="" />
              </div>
              <div>{formatStateLabel(state)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2">
        <div className="text-uppercase small opacity-75 mb-1">SHAPE - SITE TYPE</div>
        <div className="row row-cols-2 g-2">
          {types.map((type) => (
            <div key={`${type}`} className="col">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: 14, height: 14 }}
                >
                  <USGSMarker
                    type={type}
                    state={'unavailable'}
                    width={14}
                    height={14}
                    className=""
                  />
                </div>
                <div>{formatLabel(type)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <LegendAttributionDisclosure />
    </div>
  );
}
